import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../context";

const VISITOR_KEY = "isaiah_visitor_id";
// Session-scoped cache of A/B variant assignments + experiment metadata.
// Cuts the per-page-load API fan-out from 2 calls/experiment to 0 once the
// visitor has been assigned. TTL is intentionally generous (24h) because
// re-shuffling a sticky variant mid-session would invalidate impression /
// conversion attribution.
const AB_CACHE_KEY = "isaiah_ab_cache_v1";
const AB_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function _readCache() {
  try {
    const raw = localStorage.getItem(AB_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
}

function _writeCache(cache) {
  try { localStorage.setItem(AB_CACHE_KEY, JSON.stringify(cache)); } catch { /* quota or disabled */ }
}

function getCachedAssignment(experimentId) {
  const c = _readCache();
  const entry = c[experimentId];
  if (!entry || (Date.now() - (entry.cached_at || 0)) > AB_CACHE_TTL_MS) return null;
  return entry;
}

function setCachedAssignment(experimentId, variant_id, variantValue) {
  const c = _readCache();
  c[experimentId] = { variant_id, variantValue, cached_at: Date.now() };
  _writeCache(c);
}

function getVisitorId() {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = Math.random().toString(36).substring(2, 14);
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

/**
 * Hook to get assigned A/B test variant and track events.
 * @param {string} experimentId - The experiment ID to participate in
 * @returns {{ variant: string|null, variantValue: string|null, trackImpression: Function, trackConversion: Function, loading: boolean }}
 */
export function useABTest(experimentId) {
  // Hydrate from localStorage synchronously so the first render has the
  // right copy/CTA , avoids the flash of "control variant" then swap.
  const cached = experimentId ? getCachedAssignment(experimentId) : null;
  const [variant, setVariant] = useState(cached?.variant_id || null);
  const [variantValue, setVariantValue] = useState(cached?.variantValue || null);
  const [loading, setLoading] = useState(!cached);
  const visitorId = getVisitorId();

  useEffect(() => {
    if (!experimentId) { setLoading(false); return; }
    // If the cache hit on mount, skip the network entirely , the variant
    // is already set in state and the value is sticky for 24h per visitor.
    if (cached) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/ab/assign/${experimentId}?visitor_id=${visitorId}`);
        if (cancelled) return;
        const variantId = res.data.variant_id;
        setVariant(variantId);
        // Fetch experiment to get variant value
        const expRes = await axios.get(`${API}/ab/experiments/${experimentId}`);
        const exp = expRes.data;
        const v = (exp.variants || []).find(v => v.id === variantId);
        const vv = v ? (v.value || v.name) : null;
        if (!cancelled) setVariantValue(vv);
        setCachedAssignment(experimentId, variantId, vv);
      } catch (e) { console.error("AB test error:", e); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experimentId, visitorId]);

  const trackImpression = useCallback(() => {
    if (!variant || !experimentId) return;
    axios.post(`${API}/ab/track`, {
      experiment_id: experimentId,
      variant_id: variant,
      visitor_id: visitorId,
      event_type: "impression",
    }).catch(() => {});
  }, [experimentId, variant, visitorId]);

  const trackConversion = useCallback(() => {
    if (!variant || !experimentId) return;
    axios.post(`${API}/ab/track`, {
      experiment_id: experimentId,
      variant_id: variant,
      visitor_id: visitorId,
      event_type: "conversion",
    }).catch(() => {});
  }, [experimentId, variant, visitorId]);

  return { variant, variantValue, trackImpression, trackConversion, loading };
}
